import _ from 'lodash';
import { fetchService } from '../../../../utils/fetchService';
import { getCategoryFromRuleId, isBehavioral } from './helper';

// Functions for transforming the rules, aggregate data from the SARIF data
const levelOrder = {
  informational: 0, // what we show for issue level 'none'
  note: 1,
  warning: 2,
  error: 3,
};

const processRiskLevel = (kind, level) => {
  // kind defaults to fail if it doesn't exist
  if (!kind || _.lowerCase(kind) === 'fail') {
    // level defaults to warning if it's not set when kind is fail
    if (!level) {
      return 'warning';
    }

    return _.lowerCase(level) === 'none' ? 'informational' : _.lowerCase(level);
  }
  return 'informational';
};

const normalizeId = id => id.toLowerCase().trim();

export const aggregateResultsForSingleRule = (rule, optionalRuleFindingsMap = {}, processedRiskLevelNumeric = null) => {
  const { niap = [], gdpr = [] } = rule.properties?.compliance || {};
  const processedRiskLevel = Object.keys(levelOrder)[processedRiskLevelNumeric];

  const owasp = rule.properties?.compliance?.['owasp-2016'] || rule.properties?.compliance?.owasp || [];

  const normalizedRuleId = normalizeId(rule.id);

  const ruleFindingsMap = optionalRuleFindingsMap;
  if (_.isEmpty(ruleFindingsMap)) {
    ruleFindingsMap[normalizedRuleId] = [{ ruleId: normalizedRuleId, level: rule.defaultConfiguration?.level }];
  }

  // Find matching rule ids, considering rule.id could be a prefix
  const matchingRuleIds = Object.keys(ruleFindingsMap).filter(resultRuleId => resultRuleId.startsWith(normalizedRuleId));

  let aggregatedResults;

  if (matchingRuleIds.length > 0) {
    // Aggregate all results for the matching rule IDs
    const allEvidences = matchingRuleIds.flatMap(matchingRuleId => ruleFindingsMap[matchingRuleId]);

    // Determine risk level based on the first result's kind and level of severity
    const riskLevel = processedRiskLevel ?? processRiskLevel(allEvidences[0].kind, allEvidences[0].level);
    // Determine evidence and score from each result
    const confidence = allEvidences.map(evidence => evidence.properties?.confidence || 'none');
    const score = allEvidences.map(evidence => evidence.properties?.score || 0);

    aggregatedResults = {
      name: [
        rule.shortDescription?.text ??
          rule.name ??
          // first sentence of fullDescription should be descriptive enough in case both previous values are not set
          // https://docs.oasis-open.org/sarif/sarif/v2.1.0/os/sarif-v2.1.0-os.html#_Toc34317846
          rule.fullDescription?.text?.split('.')[0],
      ],
      driverName: rule.driverName || rule.tool,
      description: rule.fullDescription?.text ?? ' ',
      niap,
      owasp,
      gdpr,
      ...rule.properties,
      risk: riskLevel,
      evidences: allEvidences,
      ruleId: rule.id,
      confidence,
      score,
    };
  }

  return aggregatedResults;
};

export const getMaliciousThreatLevel = (data, isResultsService = true) => {
  if (isResultsService) {
    return data?.properties?.threatLevel ?? -1;
  }
  if (data && data.evidences && data.evidences.length > 0) {
    return data.evidences[0].properties.threatLevel;
  }
  return -1;
};

export const getMaliciousThreatTypes = (data, isResultsService = true) => {
  if (isResultsService) {
    return data?.properties?.threatTypes ?? {};
  }
  if (data && data.evidences && data.evidences.length > 0) {
    return data.evidences[0].properties.threatTypes ?? {};
  }
  return {};
};

export function transformRuleSummaries(data) {
  // count of rule categories for PDF report
  const nonBehavioralCategoriesCount = {};
  const behavioralCategoriesCount = {};

  const fullRuleSummaries = data?.map(ruleSummary => {
    const details = aggregateResultsForSingleRule(ruleSummary.rule, {}, ruleSummary.result_level_numeric);

    // Build the rule categories for PDF
    const ruleId = ruleSummary.rule?.id;
    if (ruleId) {
      const category = getCategoryFromRuleId(ruleId);
      if (details?.category !== 'behavioral') {
        nonBehavioralCategoriesCount[category] = (nonBehavioralCategoriesCount[category] || 0) + 1;
      }
      if (isBehavioral(ruleSummary)) {
        behavioralCategoriesCount[category] = (behavioralCategoriesCount[category] || 0) + 1;
      }
    }

    return {
      ...ruleSummary,
      details,
      level: details?.risk,
    };
  });

  // sort rule summaries by level then by occurrences then by name
  const levelOrderKeys = ['error', 'warning', 'note', 'informational', 'none'];
  fullRuleSummaries?.sort((a, b) => {
    const levelComparison = levelOrderKeys.indexOf(a.level?.toLowerCase()) - levelOrderKeys.indexOf(b.level?.toLowerCase());
    if (levelComparison !== 0) {
      return levelComparison;
    }
    if (a.occurrences !== b.occurrences) {
      return b.occurrences - a.occurrences;
    }
    // compare by name: sensitivity: 'base' means a ≠ b, a = á, a = A
    return a.details?.name[0].localeCompare(b.details?.name[0], undefined, { sensitivity: 'base' }) || 0;
  });

  return { fullRuleSummaries, nonBehavioralCategoriesCount, behavioralCategoriesCount };
}

// Remove duplicate rule summaries based on normalized rule ID
// Prefer the one with non-empty compliance properties
export function removeDuplicateRuleSummaries(ruleSummaries) {
  if (!Array.isArray(ruleSummaries)) return [];

  const seen = new Map();

  ruleSummaries.forEach(ruleSummary => {
    const { rule } = ruleSummary;
    const key = normalizeId(rule.shortDescription?.text || rule.id);
    const compliance = rule.properties?.compliance;
    const hasCompliance = compliance && Object.keys(compliance).length > 0;

    if (!seen.has(key)) {
      seen.set(key, ruleSummary);
    } else {
      const existing = seen.get(key);
      const existingCompliance = existing.rule.properties?.compliance;
      const existingHasCompliance = existingCompliance && Object.keys(existingCompliance).length > 0;
      // Prefer the one with non-empty compliance
      if (!existingHasCompliance && hasCompliance) {
        seen.set(key, ruleSummary);
      }
    }
  });

  return seen.size > 0 ? Array.from(seen.values()) : [];
}

export const processRules = rules => {
  rules.sort((a, b) => b.result_level_numeric - a.result_level_numeric);

  const fullRuleSummaries = rules.map(ruleSummary => {
    const details = aggregateResultsForSingleRule(ruleSummary.rule, {}, ruleSummary.result_level_numeric);
    return {
      ...ruleSummary,
      details,
      level: details?.risk,
    };
  });

  return fullRuleSummaries;
};

export const sortRegulation = obj =>
  Object.fromEntries(
    Object.entries(obj).sort(([keyA], [keyB]) =>
      keyA.localeCompare(keyB, undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
    ),
  );

export const getEvidencesCallbackWithToken = accessToken => async (analysisId, ruleGuid, page, pageSize) => {
  const results = await fetchService(
    `/eval/results/${analysisId}/evidence?ruleGuid=${ruleGuid}&page=${page}&pageSize=${pageSize}`,
    accessToken,
  );
  return { data: results };
};
